/**
 * Centralized Cache Invalidation Service
 *
 * Provides a single point of control for invalidating caches across the application.
 * This ensures consistency when cache needs to be cleared due to:
 * - User logout
 * - Profile updates
 *
 * Note: TanStack Query cache is managed by the React tree (QueryProvider).
 * These functions handle localStorage-based session/profile cache only.
 */

import { sessionCache } from '@/lib/auth/NewSessionCache';
import { logger } from '@/lib/utils/logger';

/**
 * Invalidate all caches on user logout
 */
export function invalidateOnLogout(): void {
  try {
    logger.dev('[CacheInvalidation] Clearing all caches on logout');

    // Clear session cache (auth/profile in localStorage)
    sessionCache.clear();

    logger.dev('[CacheInvalidation] All caches cleared successfully');
  } catch (error) {
    logger.error('[CacheInvalidation] Error clearing caches on logout:', error);
  }
}

/**
 * Invalidate caches when language changes
 * Some cached content may be language-specific
 */
export function invalidateOnLanguageChange(newLanguage: string): void {
  try {
    logger.dev(`[CacheInvalidation] Language changed to ${newLanguage}, invalidating affected caches`);
    // TanStack Query handles server data caching – no manual invalidation needed here
    logger.dev('[CacheInvalidation] Language-specific caches invalidated');
  } catch (error) {
    logger.error('[CacheInvalidation] Error invalidating caches on language change:', error);
  }
}

/**
 * Invalidate profile-related caches
 */
export function invalidateProfile(userId: string): void {
  try {
    logger.dev(`[CacheInvalidation] Invalidating profile cache for user ${userId}`);

    // Clear session cache for this profile
    sessionCache.invalidateProfile(userId);

    logger.dev('[CacheInvalidation] Profile cache invalidated');
  } catch (error) {
    logger.error('[CacheInvalidation] Error invalidating profile cache:', error);
  }
}
